import { computed, ref } from 'vue'

import { gratuityConfig } from '~/config/gratuityConfig'
import { useCurrencyFormat } from '~/composables/useCurrencyFormat'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import type { GratuitySelection } from '~/types/GratuitySelection'
import { gratuitySelection } from '~/utils/gratuitySelection'
import { tipLimit } from '~/utils/tipLimit'
import { tipAmountForPercent } from '~/utils/tipAmountForPercent'

const THUMB_WIDTH_PX = 16

interface GratuitySelectorOptions {
  readonly modelValue: () => GratuitySelection
  readonly subtotal: () => number | string
  readonly emitSelection: (selection: GratuitySelection) => void
}

/** The gratuity selector's view model: both values, the slider range for the active mode, quick-picks, and every input path. */
export function useGratuitySelector(options: GratuitySelectorOptions) {
  const t = usePresentationTranslator()
  const formatCurrency = useCurrencyFormat()

  const isFocused = ref(false)
  const draft = ref('')

  const isAmountMode = computed(() => options.modelValue().mode === 'amount')
  const percent = computed(() =>
    gratuitySelection.percentOf(options.modelValue(), options.subtotal())
  )
  const amount = computed(() =>
    gratuitySelection.amountOf(options.modelValue(), options.subtotal())
  )
  const percentText = computed(() => `${Number(percent.value.toFixed(2))}%`)
  const amountText = computed(() => formatCurrency(amount.value))

  // inputmode is not a declared UInput prop, so it rides as a fall-through attribute.
  const inputAttributes = computed(() => ({
    'aria-label': t('commerce.gratuitySelector.inputLabel'),
    inputmode: 'decimal',
    onFocus: handleFocus,
    onKeydown: commitOnEnter,
    type: 'text'
  }))

  // InputCurrency's generated props lack aria-label; it falls through onto the input.
  const amountInputAttributes = computed(() => ({
    'aria-label': t('commerce.gratuitySelector.amountInputLabel')
  }))

  const shownText = computed(() => (isFocused.value ? draft.value : percentText.value))

  const maxAmount = computed(() => gratuitySelection.maxAmount(options.subtotal()))

  const canSwitchToPercent = computed(() =>
    isAmountMode.value
      ? gratuitySelection.canSwitchTo(options.modelValue(), options.subtotal(), 'percent')
      : true
  )

  const sliderLabel = computed(() =>
    isAmountMode.value
      ? t('commerce.gratuitySelector.sliderAmountLabel')
      : t('commerce.gratuitySelector.sliderLabel')
  )

  const slider = computed(() =>
    isAmountMode.value
      ? {
          max: Math.max(maxAmount.value, amount.value),
          min: 0,
          snapPoints: gratuityConfig.ticks
            .map((tick) => tipAmountForPercent(options.subtotal(), tick))
            .filter((tickAmount) => tickAmount <= Math.max(maxAmount.value, amount.value)),
          snapRadius: tipAmountForPercent(options.subtotal(), gratuityConfig.snapRadius),
          step: gratuitySelection.amountStep()
        }
      : {
          max: Math.max(gratuityConfig.max, percent.value),
          min: gratuityConfig.min,
          snapPoints: gratuityConfig.ticks,
          snapRadius: gratuityConfig.snapRadius,
          step: gratuityConfig.step
        }
  )

  const sliderValue = computed(() => (isAmountMode.value ? amount.value : percent.value))

  interface GratuityTick {
    readonly ariaLabel: string
    readonly disabled: boolean
    readonly fraction: number
    readonly key: number
    readonly label: string
    readonly pressed: boolean
    readonly selection: GratuitySelection
  }

  const amountTick = (tick: number): GratuityTick => {
    const tickAmount = tipAmountForPercent(options.subtotal(), tick)
    return {
      ariaLabel: t('commerce.gratuitySelector.amountTickLabel', {
        amount: formatCurrency(tickAmount)
      }),
      disabled: false,
      fraction: tickAmount / slider.value.max,
      key: tick,
      label: formatCurrency(tickAmount),
      pressed: amount.value === tickAmount,
      selection: { amount: tickAmount, mode: 'amount' }
    }
  }

  const percentTick = (tick: number): GratuityTick => ({
    ariaLabel: t('commerce.gratuitySelector.tickLabel', { percent: tick }),
    // A percentage whose amount exceeds the tip limit cannot be charged as stated.
    disabled: tipAmountForPercent(options.subtotal(), tick) > tipLimit,
    fraction: (tick - gratuityConfig.min) / (slider.value.max - gratuityConfig.min),
    key: tick,
    label: `${tick}%`,
    pressed: percent.value === tick,
    selection: { mode: 'percent', percent: tick }
  })

  // Distinct positive amounts only: rounding can collapse small subtotals' quick-picks onto one another.
  const amountTickPercents = computed(() => {
    const seen = new Set<number>()
    return gratuityConfig.ticks.filter((tick) => {
      const tickAmount = tipAmountForPercent(options.subtotal(), tick)
      const isNew = tickAmount > 0 && tickAmount <= slider.value.max && !seen.has(tickAmount)
      seen.add(tickAmount)
      return isNew
    })
  })

  const ticks = computed<GratuityTick[]>(() =>
    isAmountMode.value
      ? amountTickPercents.value.map(amountTick)
      : gratuityConfig.ticks.map(percentTick)
  )

  // The slider thumb's centre travels min..max inset by half its width at each end.
  const tickOffset = (fraction: number): string =>
    `calc(${fraction * 100}% + ${(0.5 - fraction) * THUMB_WIDTH_PX}px)`

  const amountError = ref('')

  const select = (selection: GratuitySelection) => {
    amountError.value = ''
    options.emitSelection(gratuitySelection.withinLimit(selection, options.subtotal()))
  }

  const selectTick = (selection: GratuitySelection) => {
    select(selection)
  }

  const switchMode = (toAmount: boolean) => {
    select(
      gratuitySelection.inMode(
        options.modelValue(),
        options.subtotal(),
        toAmount ? 'amount' : 'percent'
      )
    )
  }

  const handleSlider = (value: number) => {
    select(
      isAmountMode.value
        ? { amount: gratuitySelection.roundAmount(value), mode: 'amount' }
        : { mode: 'percent', percent: value }
    )
  }

  // A cleared amount field is not a value; the last amount stays selected.
  const handleAmountInput = (value: number | null) => {
    if (value === null) return
    const entered = gratuitySelection.roundAmount(value)
    select({ amount: Math.min(tipLimit, Math.max(0, entered)), mode: 'amount' })
    amountError.value =
      entered > tipLimit
        ? t('presentation.errors.field.atMost', { max: formatCurrency(tipLimit) })
        : ''
  }

  // Text that is not a number is not a value; the last percentage stays selected.
  type ParsedPercent =
    { readonly entered: true; readonly percent: number } | { readonly entered: false }

  // The percentage input accepts the slider range and any larger percentage a mode switch already selected; the ceiling holds for the whole edit.
  const editCeiling = ref<number>(gratuityConfig.max)
  const maxPercent = computed(() =>
    isFocused.value ? editCeiling.value : Math.max(gratuityConfig.max, percent.value)
  )

  const parsePercent = (text: string): ParsedPercent => {
    const parsed = Number.parseFloat(text.replace(',', '.').replace(/[^\d.-]/gu, ''))
    return Number.isFinite(parsed)
      ? {
          entered: true,
          percent: Math.min(
            maxPercent.value,
            Math.max(gratuityConfig.min, Number(parsed.toFixed(2)))
          )
        }
      : { entered: false }
  }

  const handleInput = (text: string | number | undefined) => {
    draft.value = String(text ?? '')
    const parsed = parsePercent(draft.value)
    if (parsed.entered) select({ mode: 'percent', percent: parsed.percent })
  }

  function handleFocus() {
    editCeiling.value = Math.max(gratuityConfig.max, percent.value)
    draft.value = String(Number(percent.value.toFixed(2)))
    isFocused.value = true
  }

  const handleBlur = () => {
    isFocused.value = false
  }

  function commitOnEnter(event: KeyboardEvent) {
    if (event.key !== 'Enter') return
    event.preventDefault()
    if (event.target instanceof HTMLInputElement) event.target.blur()
  }

  return {
    amountError,
    amountInputAttributes,
    canSwitchToPercent,
    handleAmountInput,
    handleBlur,
    handleInput,
    handleSlider,
    inputAttributes,
    isAmountMode,
    amount,
    amountText,
    percentText,
    selectTick,
    shownText,
    slider,
    sliderLabel,
    sliderValue,
    switchMode,
    tickOffset,
    ticks
  }
}
